import { getDrillDownFilters } from "@components/drillDown/DrillDown.utils";
import YearSelector from "@components/inputs/date/YearSelector";
import { IInputOnChangeEvent } from "@components/inputs/input";
import { IRow, TId } from "@components/table";
import { EntitySetName, IPrTaxpayerDeclarationEntity, PrTaxpayerDeclarationEntity } from "@odata/GeneratedEntityTypes";
import { PrTaxpayerDeclarationStatusCode } from "@odata/GeneratedEnums";
import { massExportToPDF } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import React, { lazy, ReactElement, Suspense } from "react";

import { RowAction, Status } from "../../../enums";
import { ITableStorageDefaultCustomData } from "../../../model/TableStorage";
import { getUtcDate, getUtcDayjs } from "../../../types/Date";
import TableView from "../../../views/table";
import {
    ICustomButtonDefArgs,
    overrideMassExportButtonDef,
    TableButtonsAction,
    TableButtonsActionType,
    TTableToolbarItem
} from "../../../views/table/TableToolbar.utils";
import { IHandleCustomActionArgs, ITableViewBaseProps, ITableViewBaseState } from "../../../views/table/TableView";
import { getConfirmationActionText } from "../../../views/table/TableView.render.utils";
import { getErrorFromBatchResult } from "../../../views/table/TableView.utils";

const TaxpayerDeclarationMassCreateDialog = lazy(() => import("@pages/payroll/taxpayerDeclaration/taxpayerDeclarationMassCreateDialog/taxpayerDeclarationMassCreateDialog"));

interface IDeclarationCustomData extends ITableStorageDefaultCustomData {
    selectedYear?: number;
    declarationDates?: Date[];
}

interface IProps extends ITableViewBaseProps<IDeclarationCustomData> {
    isSingleEmployeeView: boolean;
}

interface IState extends ITableViewBaseState {
    isMassCreateDialogOpen: boolean;
}

const MASS_CREATE_ACTION = "massCreate";
const MASS_SIGN_ACTION = "massSign";
const MASS_SEND_TO_SIGN_ACTION = "massSendToSign";

class TaxpayerDeclarationTableView extends TableView<IProps, IState> {

    constructor(props: IProps) {
        super(props);

        this.overrideDefaultToolbarButtonDef = this.overrideDefaultToolbarButtonDef.bind(this);
    }

    get shouldRenderBreadcrumbs(): boolean {
        return !this.props.isSingleEmployeeView;
    }

    get secondaryTitle(): string {
        if (this.props.isSingleEmployeeView) {
            return this.props.storage.t("TaxpayerDeclaration:Table.SecondaryTitle");
        }
        return null;
    }

    onBeforeFetch = async (): Promise<void> => {
        if (this.props.isSingleEmployeeView) {
            return;
        }

        const res = await this.props.storage.oData.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations).query()
                .groupBy(PrTaxpayerDeclarationEntity.Year)
                .fetchData<IPrTaxpayerDeclarationEntity[]>();
        const declarations: IPrTaxpayerDeclarationEntity[] = res.value ?? [];

        const sortedDeclarations = declarations.sort((a, b) => a.Year - b.Year);

        const currentYear = getUtcDate().getFullYear();
        let thisYear: number = null;
        const declarationDates = sortedDeclarations.map((declaration) => {
            const date = getUtcDayjs().set("year", declaration.Year).toDate();
            if (currentYear === declaration.Year) {
                thisYear = declaration.Year;
            }
            return date;
        });

        const drilldownFilters = getDrillDownFilters();
        if (drilldownFilters && drilldownFilters[PrTaxpayerDeclarationEntity.Year]) {
            thisYear = parseInt(drilldownFilters[PrTaxpayerDeclarationEntity.Year] as string);
        } else if (!thisYear) {
            thisYear = declarationDates?.[0]?.getFullYear() ?? currentYear;
        }

        this.setDeclarationDateFilter(thisYear);

        this.props.storage.setCustomData({
            selectedYear: thisYear,
            declarationDates
        });
    };

    setDeclarationDateFilter = (year: number): void => {
        const filter = `${PrTaxpayerDeclarationEntity.Year} eq ${year}`;
        this.props.storage.store({ customSecondaryFilter: filter });
    };

    getToolbarButtons(): TableButtonsActionType[] {
        const buttons = super.getToolbarButtons();
        if (this.props.isSingleEmployeeView) {
            return buttons;
        }
        return [
            ...buttons,
            TableButtonsAction.MassExport,
            MASS_CREATE_ACTION,
            MASS_SEND_TO_SIGN_ACTION,
            MASS_SIGN_ACTION
        ];
    }

    get hasTabs(): boolean {
        return !this.props.isSingleEmployeeView;
    }

    getConfirmText = (tableAction: TableButtonsAction | string): string | React.ReactElement => {
        const { storage } = this.props;

        const activeRowActionCount = storage.tableAPI?.getState().activeRows.size ?? 0;
        let key: string;

        switch (tableAction) {
            case MASS_SEND_TO_SIGN_ACTION:
                key = "Send";
                break;
            case MASS_SIGN_ACTION:
                key = "Sign";
                break;
        }
        if (key) {
            return getConfirmationActionText(storage.t(`TaxpayerDeclaration:Table.${key}`), activeRowActionCount);
        }

        return super.getConfirmText(tableAction);
    };

    overrideDefaultToolbarButtonDef(defaultItem: TTableToolbarItem): TTableToolbarItem {
        return overrideMassExportButtonDef(defaultItem, this.props.storage.t("TaxpayerDeclaration:MassExportPdf.MassExportActionHeader"));
    }

    getCustomButtonDef = (type: TableButtonsActionType, {
        isActive,
        isDisabled
    }: ICustomButtonDefArgs): TTableToolbarItem => {
        const isTableEmpty = !this.props.storage.tableAPI?.getState().rowCount;

        // TODO: for now we dont want to show these actions https://solitea-cz.atlassian.net/browse/DEV-43100
        // if (type === MASS_CREATE_ACTION) {
        //     return {
        //         id: MASS_CREATE_ACTION,
        //         itemType: ToolbarItemType.Icon,
        //         iconName: "MassCreate",
        //         label: this.props.storage.t("TaxpayerDeclaration:Table.MassCreate"),
        //         order: TableActionOrder.Add + 1,
        //         isActive,
        //         isDisabled: (isDisabled && !isActive)
        //     };
        // }
        //
        // if (type === MASS_SEND_TO_SIGN_ACTION) {
        //     return {
        //         id: MASS_SEND_TO_SIGN_ACTION,
        //         itemType: ToolbarItemType.Icon,
        //         iconName: "MassSendToSign",
        //         label: this.props.storage.t("TaxpayerDeclaration:Table.MassSendToSign"),
        //         order: TableActionOrder.Add + 2,
        //         isActive,
        //         isDisabled: (isDisabled && !isActive) || isTableEmpty
        //     };
        // }

        // https://solitea-cz.atlassian.net/browse/DEV-44691
        // if (type === MASS_SIGN_ACTION) {
        //     return {
        //         id: MASS_SIGN_ACTION,
        //         itemType: ToolbarItemType.Icon,
        //         iconName: "MassSign",
        //         label: this.props.storage.t("TaxpayerDeclaration:Table.MassSign"),
        //         order: TableActionOrder.Add + 3,
        //         isActive,
        //         isDisabled: (isDisabled && !isActive) || isTableEmpty
        //     };
        // }

        return null;
    };

    isRowWithoutAction(rowId: TId, action: RowAction, row: IRow): boolean {
        if (action === RowAction.Custom) {
            const statusCode = (row.customData.entity as IPrTaxpayerDeclarationEntity).StatusCode as PrTaxpayerDeclarationStatusCode;
            if (this.getTableAction() === MASS_SIGN_ACTION) {
                return ![PrTaxpayerDeclarationStatusCode.WaitingForSignature, PrTaxpayerDeclarationStatusCode.Draft].includes(statusCode);
            } else if (this.getTableAction() === MASS_SEND_TO_SIGN_ACTION) {
                return ![PrTaxpayerDeclarationStatusCode.Draft].includes(statusCode);
            }
        }

        return super.isRowWithoutAction(rowId, action, row);
    }

    handleMassSendToSign = async (): Promise<void> => {
        await this.handleStatusChange(PrTaxpayerDeclarationStatusCode.WaitingForSignature);
    };

    handleMassSign = async (): Promise<void> => {
        await this.handleStatusChange(PrTaxpayerDeclarationStatusCode.Signed);
    };

    handleMassExport = (ids: string[]): Promise<void> => {
        return massExportToPDF(ids, this.props.storage.context);
    }

    handleMassCreate = async (): Promise<void> => {
        this.setState({
            isMassCreateDialogOpen: true
        });
        this.forceUpdate();
    };

    handleMassCreateDialogConfirm = async (): Promise<void> => {
        const { storage } = this.props;
        await this.handleMassCreateDialogClose();
        storage.setTableAlert({
            status: Status.Success,
            title: storage.t("Components:Table.UpdateOk"),
            subTitle: storage.t("TaxpayerDeclaration:Table.MassCreateSuccess"),
            useFade: true
        });
        await storage.tableAPI.reloadTable();
    };

    handleMassCreateDialogClose = async (): Promise<void> => {
        this.setState({
            isMassCreateDialogOpen: false
        });
        await this.setTableAction(null);
    };

    handleStatusChange = async (statusCode: PrTaxpayerDeclarationStatusCode): Promise<void> => {
        const { storage } = this.props;
        const selectedRows = await storage.tableAPI.getAffectedRows();
        const openedFormKey = this.props.formStorage?.data.bindingContext.getKey();
        let formRefreshNeeded = false;

        const batch = storage.oData.batch();
        batch.beginAtomicityGroup("massSign");

        for (const row of selectedRows) {
            if (parseInt(row.key) === openedFormKey) {
                formRefreshNeeded = true;
            }
            batch.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations)
                    .update(row.key, {
                        [PrTaxpayerDeclarationEntity.StatusCode]: statusCode
                    });
        }

        const batchResponse = await batch.execute();

        let alert = getErrorFromBatchResult(batchResponse);
        if (!alert) {
            alert = {
                status: Status.Success,
                title: storage.t("Components:Table.UpdateOk"),
                subTitle: storage.t(`TaxpayerDeclaration:Table.${statusCode}Success`),
                useFade: true
            };
        }
        storage.setTableAlert(alert);

        this.setTableAction(null);
        await this.props.storage.tableAPI.reloadTable();
        if (formRefreshNeeded) {
            this.props.formStorage.reload();
        }
    };

    handleCustomAction(action: string, args: IHandleCustomActionArgs = {}): void {
        if (action === MASS_CREATE_ACTION) {
            this.handleMassCreate();
        } else {
            super.handleCustomAction(action, args);
        }
    }

    async handleToolbarConfirm(): Promise<void> {
        if (this.isTableAction(MASS_SIGN_ACTION)) {
            return this.handleMassSign();
        }
        if (this.isTableAction(MASS_SEND_TO_SIGN_ACTION)) {
            return this.handleMassSendToSign();
        }
        return super.handleToolbarConfirm();
    }

    handleYearChange = (e: IInputOnChangeEvent<Date>): void => {
        const year = getUtcDayjs(e.value).get("year");
        this.setDeclarationDateFilter(year);
        this.props.storage.setCustomData({ selectedYear: year });
        this.forceUpdate();
    };

    renderTabs(): ReactElement {
        const storage = this.props.storage;
        const dates = storage.getCustomData().declarationDates ?? [];
        const value = this.props.storage.getCustomData().selectedYear;
        if (!value) {
            return null;
        }
        return <YearSelector
                value={getUtcDayjs().set("year", value).toDate()}
                onChange={this.handleYearChange}
                minDate={dates[0] ?? getUtcDayjs().subtract(1, "year").toDate()}
                isMain={true}
        />;
    }

    renderMassCreateDialog = (): React.ReactElement => {
        return (
                <Suspense fallback={<></>}>
                    <TaxpayerDeclarationMassCreateDialog
                            onConfirm={this.handleMassCreateDialogConfirm}
                            onCancel={this.handleMassCreateDialogClose}
                            rootStorage={this.props.storage}
                    />
                </Suspense>
        );
    };

    render() {
        return (
                <>
                    {this.state.isMassCreateDialogOpen && this.renderMassCreateDialog()}
                    {super.render()}
                </>
        );
    }
}

export default TaxpayerDeclarationTableView;